import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import {
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InboxArrowDownIcon,
} from "@heroicons/react/24/outline"
import { Phone } from "lucide-react"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

const statusBadgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors",
  {
    variants: {
      variant: {
        default: "bg-secondary text-secondary-foreground",
        success: "bg-success/10 text-success",
        warning: "bg-warning/10 text-warning",
        destructive: "bg-destructive/10 text-destructive",
        info: "bg-info/10 text-info",
        muted: "bg-muted text-muted-foreground",
        outline: "border border-border bg-transparent text-foreground",
      },
      size: {
        default: "px-2.5 py-0.5 text-xs",
        sm: "px-2 py-0.5 text-[10px]",
        lg: "px-3 py-1 text-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface StatusBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof statusBadgeVariants> {
  dot?: boolean
  icon?: React.ReactNode
}

const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps>(
  ({ className, variant, size, dot, icon, children, ...props }, ref) => {
    const getDotColor = () => {
      switch (variant) {
        case "success":
          return "bg-success"
        case "warning":
          return "bg-warning"
        case "destructive":
          return "bg-destructive"
        case "info":
          return "bg-info"
        default:
          return "bg-muted-foreground"
      }
    }

    return (
      <span
        ref={ref}
        className={cn(statusBadgeVariants({ variant, size }), className)}
        {...props}
      >
        {dot && (
          <span className={cn("h-1.5 w-1.5 rounded-full", getDotColor())} />
        )}
        {icon && <span className="shrink-0">{icon}</span>}
        {children}
      </span>
    )
  }
)
StatusBadge.displayName = "StatusBadge"

// Pre-configured status badges for common use cases
const CallStatusBadge = ({ status }: { status: string }) => {
  const config: Record<string, { variant: "success" | "warning" | "destructive" | "muted" | "info"; label: string }> = {
    completed: { variant: "success", label: "Completed" },
    in_progress: { variant: "info", label: "In Progress" },
    scheduled: { variant: "muted", label: "Scheduled" },
    no_answer: { variant: "warning", label: "No Answer" },
    voicemail: { variant: "warning", label: "Voicemail" },
    failed: { variant: "destructive", label: "Failed" },
    cancelled: { variant: "muted", label: "Cancelled" },
  }

  const { variant, label } = config[status] || { variant: "muted" as const, label: status }

  return (
    <StatusBadge variant={variant} dot>
      {label}
    </StatusBadge>
  )
}

const CampaignStatusBadge = ({ status }: { status: string }) => {
  const config: Record<string, { variant: "success" | "warning" | "destructive" | "muted" | "info"; label: string }> = {
    active: { variant: "success", label: "Active" },
    paused: { variant: "warning", label: "Paused" },
    scheduled: { variant: "info", label: "Scheduled" },
    completed: { variant: "muted", label: "Completed" },
    draft: { variant: "muted", label: "Draft" },
  }

  const { variant, label } = config[status] || { variant: "muted" as const, label: status }

  return (
    <StatusBadge variant={variant} dot>
      {label}
    </StatusBadge>
  )
}

const ConsentStatusBadge = ({ consented }: { consented: boolean | null }) => {
  if (consented === true) {
    return <StatusBadge variant="success" dot>Consented</StatusBadge>
  }
  if (consented === false) {
    return <StatusBadge variant="destructive" dot>Declined</StatusBadge>
  }
  return <StatusBadge variant="muted" dot>Pending</StatusBadge>
}

/**
 * Rich consent status badge that handles all backend ConsentStatus values:
 * CONSENTED, DECLINED, UNCERTAIN, NOT_ASKED, plus null/undefined (no record).
 * Optionally shows consent method and action-required indicator.
 */
const ConsentStatusRichBadge = ({
  status,
  method,
  needsAction,
}: {
  status: string | null | undefined
  method?: string | null
  needsAction?: boolean
}) => {
  const normalized = status?.toUpperCase()?.replace(/\s+/g, "_") || ""

  const config: Record<string, { variant: "success" | "warning" | "destructive" | "muted" | "info"; label: string }> = {
    CONSENTED: { variant: "success", label: "Consented" },
    ACTIVE: { variant: "success", label: "Consented" },
    DECLINED: { variant: "destructive", label: "Declined" },
    PATIENT_DECLINED: { variant: "destructive", label: "Declined" },
    UNCERTAIN: { variant: "warning", label: "Uncertain" },
    NOT_ASKED: { variant: "muted", label: "Not Asked" },
  }

  const { variant, label } = config[normalized] || { variant: "muted" as const, label: status || "None" }

  const formatMethod = (m: string): string => {
    // Convert snake_case/camelCase to Title Case
    return m
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      <StatusBadge variant={variant} dot>
        {label}
      </StatusBadge>
      {method && (
        <span className="text-[10px] text-muted-foreground">
          {formatMethod(method)}
        </span>
      )}
      {needsAction && normalized !== "CONSENTED" && normalized !== "ACTIVE" && (
        <span className="inline-flex h-1.5 w-1.5 rounded-full bg-warning" title="Action required" />
      )}
    </span>
  )
}

/**
 * Call status as a centered icon with tooltip — matches the CallsTable icons.
 */
const CallStatusIcon = ({ status, className }: { status: string; className?: string }) => {
  const statusLower = status?.toLowerCase() || ""

  const iconConfig: Record<string, { icon: React.ReactNode; label: string }> = {
    completed: {
      icon: <CheckCircleIcon className={cn("h-5 w-5 text-success", className)} />,
      label: "Completed",
    },
    pending: {
      icon: <ClockIcon className={cn("h-5 w-5 text-warning", className)} />,
      label: "Pending",
    },
    in_progress: {
      icon: <Phone className={cn("h-5 w-5 text-info animate-pulse", className)} />,
      label: "In Progress",
    },
    failed: {
      icon: <XCircleIcon className={cn("h-5 w-5 text-destructive", className)} />,
      label: "Failed",
    },
    voicemail: {
      icon: <InboxArrowDownIcon className={cn("h-5 w-5 text-muted-foreground", className)} />,
      label: "Voicemail",
    },
  }

  const { icon, label } = iconConfig[statusLower] || {
    icon: <ExclamationTriangleIcon className={cn("h-5 w-5 text-muted-foreground", className)} />,
    label: status || "Unknown",
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {icon}
        </TooltipTrigger>
        <TooltipContent>
          <p>{label}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export {
  StatusBadge,
  statusBadgeVariants,
  CallStatusBadge,
  CallStatusIcon,
  CampaignStatusBadge,
  ConsentStatusBadge,
  ConsentStatusRichBadge
}
